import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import Breadcrumb from '../components/ui/Breadcrumb';

const eventLabels = {
  created: { label: 'Creado',   color: 'badge-success' },
  updated: { label: 'Editado',  color: 'badge-info' },
  deleted: { label: 'Eliminado', color: 'badge-danger' },
};

const moduleLabels = {
  'case-files':   'Expedientes',
  'clients':      'Clientes',
  'budgets':      'Presupuestos',
  'appointments': 'Citas',
};

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    causer_id: '',
    date_from: '',
    date_to: '',
  });

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (error) { console.error(error); }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.type) params.subject_type = filters.type;
      if (filters.causer_id) params.causer_id = filters.causer_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      const res = await api.get('/activity-log', { params });
      setLogs(res.data.data ?? res.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchUsers();
    fetchLogs();
  }, []);

  const handleFilter = (e) => {
    e.preventDefault();
    fetchLogs();
  };

  const handleReset = () => {
    setFilters({ type: '', causer_id: '', date_from: '', date_to: '' });
    setTimeout(fetchLogs, 100);
  };

  return (
    <div className="container-fluid">
      <Breadcrumb items={[{ label: 'Log de Auditoría' }]} />

      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Log de Auditoría</h4>
        <span className="badge badge-secondary">{logs.length} registros</span>
      </div>

      {/* Filtros */}
      <div className="card mb-4">
        <div className="card-body">
          <form onSubmit={handleFilter}>
            <div className="row">
              <div className="col-12 col-md-3">
                <div className="form-group mb-2">
                  <label className="small text-muted">Módulo</label>
                  <select className="form-control form-control-sm"
                    value={filters.type}
                    onChange={e => setFilters({ ...filters, type: e.target.value })}>
                    <option value="">Todos</option>
                    {Object.entries(moduleLabels).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-12 col-md-3">
                <div className="form-group mb-2">
                  <label className="small text-muted">Usuario</label>
                  <select className="form-control form-control-sm"
                    value={filters.causer_id}
                    onChange={e => setFilters({ ...filters, causer_id: e.target.value })}>
                    <option value="">Todos</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-6 col-md-2">
                <div className="form-group mb-2">
                  <label className="small text-muted">Desde</label>
                  <input type="date" className="form-control form-control-sm"
                    value={filters.date_from}
                    onChange={e => setFilters({ ...filters, date_from: e.target.value })} />
                </div>
              </div>
              <div className="col-6 col-md-2">
                <div className="form-group mb-2">
                  <label className="small text-muted">Hasta</label>
                  <input type="date" className="form-control form-control-sm"
                    value={filters.date_to}
                    onChange={e => setFilters({ ...filters, date_to: e.target.value })} />
                </div>
              </div>
              <div className="col-12 col-md-2 d-flex align-items-end">
                <div className="d-flex flex-wrap mb-2" style={{ gap: '6px' }}>
                  <button type="submit" className="btn btn-primary btn-sm">Filtrar</button>
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleReset}>Limpiar</button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Tabla */}
      <div className="card">
        <div className="card-body p-0" style={{ overflowX: 'auto' }}>
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
          ) : logs.length === 0 ? (
            <div className="text-center text-muted py-5">No hay registros de auditoría</div>
          ) : (
            <table className="table table-hover mb-0" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ width: '10%' }}>Acción</th>
                  <th style={{ width: '12%' }}>Módulo</th>
                  <th style={{ width: '8%' }}>ID</th>
                  <th style={{ width: '15%' }}>Usuario</th>
                  <th style={{ width: '35%' }}>Cambios</th>
                  <th style={{ width: '15%' }}>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id}>
                    <td>
                      <span className={`badge ${eventLabels[log.event]?.color}`}>
                        {eventLabels[log.event]?.label}
                      </span>
                    </td>
                    <td><small>{moduleLabels[log.subject_type] || log.subject_type}</small></td>
                    <td><small className="text-muted">#{log.subject_id}</small></td>
                    <td><small>{log.causer}</small></td>
                    <td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                      {log.old && log.new ? (
                        <div style={{ fontSize: '11px' }}>
                          {Object.keys(log.new).map(key => (
                            log.old[key] !== log.new[key] ? (
                              <div key={key} className="mb-1">
                                <span className="text-muted">{key}:</span>{' '}
                                <span className="text-danger" style={{ textDecoration: 'line-through' }}>{String(log.old[key] ?? '—')}</span>
                                {' → '}
                                <span className="text-success">{String(log.new[key] ?? '—')}</span>
                              </div>
                            ) : null
                          ))}
                        </div>
                      ) : (
                        <small className="text-muted">{log.description}</small>
                      )}
                    </td>
                    <td>
                      <small className="text-muted">
                        {new Date(log.created_at).toLocaleDateString('es-MX', {
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLog;
